import { ReactNode, useMemo, useRef } from 'react';
import styles from './FiltersLayout.css';
import { Filter } from './components/Filter';
import { Group } from './components/Group';
import { TFilter } from 'shared/types/filters';
import { useIsNarrowFilters } from 'shared/hooks';
import clsx from 'clsx';

interface IFilter {
  id: TFilter;
  component: ReactNode;
}

interface IFiltersLayoutProps {
  filters: IFilter[];
  applyButton: React.ReactNode;
  searchButton: React.ReactNode;
  tags: React.ReactNode;
  isRedesign: boolean;
}

export const FiltersLayout: React.FC<IFiltersLayoutProps> = ({
  filters,
  applyButton,
  tags,
  searchButton,
  isRedesign,
}) => {
  const filtersRef = useRef<HTMLDivElement>(null);
  const isNarrow = useIsNarrowFilters(filtersRef, filters);

  const { mainFilters, geoFilter, advancedFiltersFilter } = useMemo(() => {
    if (isRedesign) {
      return {
        mainFilters: filters.filter(({ id }) => id !== 'advancedFilters'),
        geoFilter: null,
        advancedFiltersFilter: filters.find(({ id }) => id === 'advancedFilters')?.component,
      };
    } else {
      return {
        mainFilters: filters.filter(({ id }) => id !== 'geo'),
        geoFilter: filters.find(({ id }) => id === 'geo')?.component,
        advancedFiltersFilter: null,
      };
    }
  }, [filters, isRedesign]);

  return (
    <div className={isRedesign ? styles['redesigned-container'] : styles['container']}>
      <div className={styles['layout']}>
        <div
          ref={filtersRef}
          className={clsx(styles['filters'], isRedesign && styles['redesigned-filters'])}
          data-narrow={isNarrow}
        >
          <Group isRedesign={isRedesign}>
            {mainFilters.map(({ id, component }) => (
              <Filter key={`filter_${id}`} dataTestId={id} isRedesign={isRedesign}>
                {component}
              </Filter>
            ))}
          </Group>

          <Group isRedesign={isRedesign}>
            {isRedesign ? (
              <Filter className={styles['advancedFilters']}>{advancedFiltersFilter}</Filter>
            ) : (
              <Filter className={styles['geo']}>{geoFilter}</Filter>
            )}

            <div className={styles['button']}>{applyButton}</div>

            <div className={styles['button']}>{searchButton}</div>
          </Group>
        </div>
        <div className={styles['tags']}>{tags}</div>
      </div>
    </div>
  );
};
